import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ChevronRight, Search, GraduationCap, Users, Loader } from 'lucide-react';
import { fetchDosen, fetchTendik, getImageUrl } from '../utils/api';

const DEPT_OPTIONS = ['Semua', 'Kesehatan Masyarakat', 'Keperawatan', 'Farmasi', 'Profesi Ners', 'Tenaga Kependidikan'];
const ITEMS_PER_PAGE = 12;

const PRODI_COLOR_MAP = {
  kesmas:      'from-orange-400 to-amber-500',    // Kuning
  farmasi:     'from-blue-600 to-blue-800',        // Biru
  keperawatan: 'from-emerald-600 to-teal-700',    // Hijau
  ners:        'from-rose-500 to-red-700',         // Merah
};
const TENDIK_COLOR = 'from-[#7B2FBE] to-[#9333EA]'; // Ungu

const getStaffColor = (staff) =>
  staff.type === 'tendik'
    ? TENDIK_COLOR
    : (PRODI_COLOR_MAP[staff.prodi] || 'from-[#7B2FBE] to-[#9333EA]');

const PRODI_MAP = {
  kesmas: 'Kesehatan Masyarakat',
  keperawatan: 'Keperawatan',
  farmasi: 'Farmasi',
  ners: 'Profesi Ners',
};

const getInitials = (name) =>
  name?.replace(/^(Dr\.|apt\.|Ns\.|Drs\.|S\.Kep\.)?\s*/, '').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '??';

const Pagination = ({ current, total, onChange }) => {
  if (total <= 1) return null;
  const pages = [];
  const delta = 2;
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return (
    <div className="flex items-center justify-center gap-1 mt-10">
      <button
        onClick={() => onChange(current - 1)}
        disabled={current === 1}
        className="p-2 rounded-lg disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
      >
        <ChevronRight size={18} className="rotate-180" />
      </button>
      {pages.map((p, i) =>
        p === '...' ? (
          <span key={i} className="px-2 text-gray-400">...</span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
              current === p
                ? 'bg-purple-600 text-white'
                : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300'
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => onChange(current + 1)}
        disabled={current === total}
        className="p-2 rounded-lg disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
};

const DosenTendik = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // State diambil dari URL query params — otomatis pulih saat back button
  const search     = searchParams.get('q')    || '';
  const activeDept = searchParams.get('dept') || 'Semua';
  const page       = parseInt(searchParams.get('page') || '1', 10);

  const setSearch = (val) => setSearchParams(p => {
    const next = new URLSearchParams(p);
    if (val) next.set('q', val); else next.delete('q');
    next.set('page', '1');
    return next;
  });

  const setActiveDept = (val) => setSearchParams(p => {
    const next = new URLSearchParams(p);
    if (val && val !== 'Semua') next.set('dept', val); else next.delete('dept');
    next.set('page', '1');
    return next;
  });

  const setPage = (val) => setSearchParams(p => {
    const next = new URLSearchParams(p);
    next.set('page', String(val));
    return next;
  });

  const [dosenList, setDosenList] = useState([]);
  const [tendikList, setTendikList] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const [dosen, tendik] = await Promise.all([fetchDosen(), fetchTendik()]);
      setDosenList(dosen);
      setTendikList(tendik);
      setLoading(false);
    };
    loadData();
  }, []);

  const allStaff = useMemo(() => [
    ...dosenList.map(d => ({
      id: d.id,
      name: d.nama,
      nidn: d.nidn || '',
      nuptk: d.nuptk || '',
      nik: d.nik || '',
      dept: PRODI_MAP[d.prodi] || d.prodi || '-',
      prodi: d.prodi,
      title: d.jabatan_fungsional || '-',
      foto: d.foto,
      slug: d.slug,
      type: 'dosen',
    })),
    ...tendikList.map(t => ({
      id: t.id,
      name: t.nama,
      nip: t.nip || '',
      nitk: t.nitk || '',
      dept: t.unit || '-',
      title: t.jabatan || '-',
      foto: t.foto,
      slug: t.slug,
      type: 'tendik',
    })),
  ], [dosenList, tendikList]);

  const filtered = useMemo(() => {
    let list = allStaff;
    if (activeDept === 'Tenaga Kependidikan') {
      list = list.filter(s => s.type === 'tendik');
    } else if (activeDept !== 'Semua') {
      list = list.filter(s => s.dept === activeDept);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.dept?.toLowerCase().includes(q) ||
        s.title?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [search, activeDept, allStaff]);



  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const dosenCount = dosenList.length;
  const tendikCount = tendikList.length;

  const handleCardClick = (staff) => {
    const identifier = staff.slug || staff.id;
    if (staff.type === 'dosen') {
      navigate(`/dosen/${identifier}`);
    } else {
      navigate(`/tendik/${identifier}`);
    }
  };

  return (
    <div data-testid="dosen-tendik-page">
      <div className="page-hero pt-[70px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
          <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
            <Link to="/" className="hover:text-white flex items-center gap-1">
              <Home size={14} /> Beranda
            </Link>
            <ChevronRight size={14} />
            <span className="text-white">Dosen & Tendik</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Dosen & Tenaga Kependidikan
          </h1>
          <p className="text-white/80">
            SDM profesional dan berdedikasi STIKES Harapan Ibu Jambi
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { icon: GraduationCap, label: 'Total Dosen', value: dosenCount },
            { icon: Users, label: 'Tenaga Kependidikan', value: tendikCount },
            { icon: Users, label: 'Total SDM', value: dosenCount + tendikCount },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="glass-card p-4 text-center rounded-xl"
            >
              <stat.icon className="mx-auto mb-2 text-purple-500" size={24} />
              <div className="text-2xl font-bold text-gray-800 dark:text-white">{stat.value}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Cari nama, keahlian, atau prodi..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {DEPT_OPTIONS.map(dept => (
            <button
              key={dept}
              onClick={() => setActiveDept(dept)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeDept === dept
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-purple-100'
              }`}
            >
              {dept}
            </button>
          ))}
        </div>

        {!loading && (
          <p className="text-sm text-gray-400 dark:text-gray-500 mb-4">
            Menampilkan {paginated.length} dari {filtered.length} data
            {search && ` untuk "${search}"`}
          </p>
        )}

        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader className="animate-spin text-purple-500" size={40} />
            <span className="ml-3 text-gray-500">Memuat data...</span>
          </div>
        )}

        {!loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {paginated.map((staff, i) => (
              <motion.div
                key={staff.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="glass-card rounded-2xl overflow-hidden hover:shadow-xl transition-all cursor-pointer hover:-translate-y-1"
                onClick={() => handleCardClick(staff)}
              >
                <div className={`h-32 bg-gradient-to-br ${getStaffColor(staff)} flex items-center justify-center relative`}>
                  {staff.foto ? (
                    <img
                      src={getImageUrl(staff.foto)}
                      alt={staff.name}
                      className="w-20 h-20 rounded-full object-cover border-4 border-white shadow"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold border-4 border-white/30">
                      {getInitials(staff.name)}
                    </div>
                  )}
                  <span className={`absolute top-2 right-2 text-xs px-2 py-1 rounded-full font-medium ${
                    staff.type === 'dosen' ? 'bg-purple-500 text-white' : 'bg-teal-500 text-white'
                  }`}>
                    {staff.type === 'dosen' ? 'Dosen' : 'Tendik'}
                  </span>
                </div>

                <div className="p-4">
                  <h3 className="font-semibold text-gray-800 dark:text-white text-sm leading-tight mb-1 line-clamp-2">
                    {staff.name}
                  </h3>
                  <p className="text-purple-600 dark:text-purple-400 text-xs font-medium mb-1 line-clamp-1">
                    {staff.title}
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs mb-2">
                    {staff.dept}
                  </p>
                  {staff.type === 'dosen' ? (
                    <div className="space-y-0.5">
                      {staff.nidn && <p className="text-gray-400 text-xs">NIDN: {staff.nidn}</p>}
                      {staff.nuptk && <p className="text-gray-400 text-xs">NUPTK: {staff.nuptk}</p>}
                      {!staff.nidn && !staff.nuptk && staff.nik && <p className="text-gray-400 text-xs">NIK: {staff.nik}</p>}
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      {staff.nip && <p className="text-gray-400 text-xs">NIP: {staff.nip}</p>}
                      {staff.nitk && <p className="text-gray-400 text-xs">NITK: {staff.nitk}</p>}
                    </div>
                  )}
                  <p className="text-xs text-purple-400 mt-3 opacity-70">Klik untuk detail →</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-20 text-gray-500">
            <Users size={48} className="mx-auto mb-4 opacity-30" />
            <p>Tidak ada data yang ditemukan</p>
          </div>
        )}

        <Pagination
          current={page}
          total={totalPages}
          onChange={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        />
      </div>
    </div>
  );
};

export default DosenTendik;